import React, { useLayoutEffect, useRef, useState } from 'react';
import { cn } from '../../lib/cn';
import { Icon } from '../Icon/Icon';
import type { WeekAppointment, WeekDay } from '../../calendar/types';
import { WeekAppointmentCell } from './WeekAppointmentCell';

/** Reference week grid — one hour slot height (px). */
const WEEK_ROW_HEIGHT = 66;

/** Don't open the grid before this, even if a bad time parses as midnight. */
const EARLIEST_WEEK_HOUR = 5;

/** Pull the grid start up so an appointment before 8:00 gets its own row
 * instead of painting above the body and tucking under the day header. */
function resolveWeekStart(appointments: WeekAppointment[], startHour: number): number {
  let earliest = startHour;
  for (const appt of appointments) {
    if (!Number.isFinite(appt.hour)) continue;
    const floored = Math.floor(appt.hour);
    if (floored >= EARLIEST_WEEK_HOUR && floored < earliest) earliest = floored;
  }
  return earliest;
}

export interface WeekGridProps {
  days: WeekDay[];
  appointments: WeekAppointment[];
  onSelectAppointment?: (appt: WeekAppointment, day: WeekDay) => void;
  className?: string;
  startHour?: number;
  endHour?: number;
  nowHour?: number;
}

export function WeekGrid({
  days,
  appointments,
  onSelectAppointment,
  className,
  startHour = 8,
  endHour = 19,
  nowHour = 14.5,
}: WeekGridProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const gridStart = resolveWeekStart(appointments, startHour);
  const hours = Array.from({ length: endHour - gridStart + 1 }, (_, i) => gridStart + i);
  const hourCount = hours.length;
  const timeCol = 52;
  const todayDay = days.find((d) => d.isToday);
  const [rowHeight, setRowHeight] = useState(WEEK_ROW_HEIGHT);

  const gridBodyHeight = rowHeight * hourCount;
  const nowOffset = (nowHour - gridStart + 0.5) * rowHeight;

  useLayoutEffect(() => {
    const scrollEl = scrollRef.current;
    if (!scrollEl) return;

    const syncRowHeight = () => {
      const viewportH = scrollEl.clientHeight;
      const minGridH = WEEK_ROW_HEIGHT * hourCount;
      const targetGridH = Math.max(minGridH, viewportH);
      setRowHeight(targetGridH / hourCount);
    };

    syncRowHeight();
    const ro = new ResizeObserver(syncRowHeight);
    ro.observe(scrollEl);
    return () => ro.disconnect();
  }, [hourCount]);

  useLayoutEffect(() => {
    const scrollEl = scrollRef.current;
    if (!scrollEl) return;
    scrollEl.scrollTop = Math.max(0, nowOffset - scrollEl.clientHeight * 0.38);
  }, [nowOffset]);

  return (
    <div
      className={cn(
        'cal-fade flex h-full min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-[var(--r-xl)] border border-[var(--cream-2)] bg-[var(--cream-1)] shadow-[var(--sh-2)]',
        className,
      )}
      style={{ animationDelay: '150ms' }}
    >
      <div className="calendar-header-band flex shrink-0">
        <div className="shrink-0" style={{ width: timeCol }} />
        {days.map((d) => (
          <div
            key={d.id}
            className={cn(
              'relative min-w-0 flex-1 border-l border-[var(--calendar-header-line)] px-3 py-[11px]',
              d.isToday && 'bg-[var(--current-day-bg)]',
            )}
          >
            {d.isToday && (
              <span className="absolute left-3 right-3 top-0 h-[2.5px] rounded-b-[3px] bg-[var(--current-day-border)]" />
            )}
            <div className="flex items-baseline gap-1.5">
              <span
                className={cn(
                  'text-[11px] font-semibold uppercase tracking-[0.04em]',
                  d.isToday ? 'text-[var(--current-day-text)]' : 'text-[var(--calendar-header-muted)]',
                )}
              >
                {d.short}
              </span>
              <span
                className={cn(
                  'font-[var(--font-display)] text-[17px] font-bold tracking-[-0.025em]',
                  d.isToday ? 'text-[var(--current-day-text)]' : 'text-[var(--calendar-header-ink)]',
                )}
              >
                {d.num}
              </span>
              {d.isToday && (
                <span className="ml-auto rounded-full bg-[color-mix(in_srgb,var(--current-day-border)_18%,transparent)] px-[5px] py-px text-[8.5px] font-bold tracking-[0.04em] text-[var(--current-day-text)]">
                  NOW
                </span>
              )}
            </div>
            <div className="mt-[5px] flex items-center gap-[7px] text-[10.5px]">
              <span className="font-[var(--font-mono)] font-semibold text-[var(--calendar-header-metric)]">
                ${(d.production / 1000).toFixed(1)}k
              </span>
              <span className="text-[var(--calendar-header-faint)]">·</span>
              <span
                className={cn(
                  'font-[var(--font-mono)] font-semibold',
                  d.fillPct >= 80 ? 'text-[var(--calendar-header-ink)]' : d.fillPct >= 50 ? 'text-[var(--calendar-header-metric)]' : 'text-[var(--calendar-header-muted)]',
                )}
              >
                {d.fillPct}%
              </span>
              {d.atRisk > 0 && (
                <span className="ml-auto inline-flex items-center gap-[3px] font-semibold text-[var(--terracotta)]">
                  <Icon kind="alert" size={10} stroke={2.4} />
                  {d.atRisk}
                </span>
              )}
            </div>
          </div>
        ))}
      </div>

      <div ref={scrollRef} className="cal-scroll relative min-h-0 flex-1 overflow-auto">
        <div className="relative flex" style={{ height: gridBodyHeight, minHeight: '100%' }}>
          <div
            className="sticky left-0 z-[2] shrink-0 border-r border-[var(--cream-2)] bg-[var(--cream-1)]"
            style={{ width: timeCol }}
          >
            {hours.map((h, i) => (
              <div
                key={h}
                className={cn(
                  'flex items-start justify-end pr-[9px] pt-1',
                  i < hours.length - 1 && 'border-b border-[var(--cream-2)]',
                )}
                style={{ height: rowHeight }}
              >
                <span className="font-[var(--font-mono)] text-[10px] font-medium text-[var(--ink-4)]">
                  {h > 12 ? h - 12 : h} {h >= 12 ? 'PM' : 'AM'}
                </span>
              </div>
            ))}
          </div>

          {days.map((d) => (
            <div
              key={d.id}
              className="relative min-w-0 flex-1 border-l border-[var(--cream-2)]"
              style={{ height: gridBodyHeight }}
            >
              {hours.map((h, i) => (
                <div
                  key={h}
                  className={cn(
                    i < hours.length - 1 && 'border-b border-[color-mix(in_srgb,var(--cream-2)_60%,transparent)]',
                  )}
                  style={{ height: rowHeight }}
                />
              ))}
              {appointments
                .filter((a) => a.dayId === d.id)
                .map((a) => {
                  const slotIndex = a.hour - gridStart;
                  const top = Math.max(3, slotIndex * rowHeight + 3);
                  return (
                    <div
                      key={a.id}
                      className="absolute z-[1] overflow-hidden"
                      style={{
                        top,
                        height: rowHeight - 6,
                        left: 4,
                        right: 4,
                      }}
                    >
                      <WeekAppointmentCell
                        appt={a}
                        onClick={() => onSelectAppointment?.(a, d)}
                      />
                    </div>
                  );
                })}
              {d.isToday && todayDay && (
                <div
                  className="pointer-events-none absolute z-[3] h-[1.5px] bg-[var(--current-time)]"
                  style={{
                    top: nowOffset,
                    left: 0,
                    right: 0,
                  }}
                >
                  <span className="absolute -left-[5px] -top-[4.5px] h-2.5 w-2.5 rounded-full border-2 border-[var(--cream-1)] bg-[var(--current-time)]" />
                  <span className="absolute right-[5px] -top-2 rounded bg-[var(--current-time)] px-[5px] py-px font-[var(--font-mono)] text-[8.5px] font-bold tracking-[0.05em] text-white">
                    2:30
                  </span>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
